import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageCircle, Ticket, HelpCircle, Mail, Phone, Plus, Send, ChevronDown, Clock, ShieldCheck, MessageSquareText,
} from 'lucide-react';
import { useToast } from '../components/ToastProvider';
import { getTickets, getTicket, createTicket, addTicketComment } from '../api/ticketApi';
import { getChatMessages, sendChatMessage } from '../api/chatApi';

const FAQS = [
  { q: 'How long does it take to get a response?', a: 'Our team typically responds within 24 hours on business days.' },
  { q: 'How do I update my gym listing?', a: 'Go to My Gym from the sidebar and edit any section, then save changes.' },
  { q: 'When are payouts processed?', a: 'Payouts are processed weekly, every Monday, to your registered bank account.' },
];

const QUICK_QUESTIONS = ["How do I update my pricing?", "Why is my listing not verified?", "How do I cancel a booking on a member's behalf?", 'Can I change my payout bank account?'];

const CATEGORY_OPTIONS = [
  { value: 'payment', label: 'Payment' },
  { value: 'technical', label: 'Technical' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  'in-progress': 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  closed: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
};
const STATUS_LABEL = { open: 'Open', 'in-progress': 'In Progress', closed: 'Closed' };

function ticketNumber(id) {
  return `TCK-${id.slice(-6).toUpperCase()}`;
}

function formatRelativeTime(value) {
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function SupportTab({ onOpenChat }) {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-8 space-y-4">
        <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-4">Get in Touch</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href="mailto:support@upnoraa.com" className="p-4 rounded-2xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043] text-center hover:bg-[#f1f3f4] dark:hover:bg-[#202124]">
              <Mail className="w-5 h-5 mx-auto text-[#1a73e8] mb-1.5" />
              <span className="text-[10px] font-semibold text-[#9aa0a6] uppercase block">Email</span>
              <span className="text-xs font-medium text-[#202124] dark:text-[#e8eaed]">support@upnoraa.com</span>
            </a>
            <a href="tel:+919876543210" className="p-4 rounded-2xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043] text-center hover:bg-[#f1f3f4] dark:hover:bg-[#202124]">
              <Phone className="w-5 h-5 mx-auto text-[#34A853] mb-1.5" />
              <span className="text-[10px] font-semibold text-[#9aa0a6] uppercase block">Phone</span>
              <span className="text-xs font-medium text-[#202124] dark:text-[#e8eaed]">+91 98765 43210</span>
            </a>
            <button onClick={onOpenChat} className="p-4 rounded-2xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043] text-center hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer">
              <MessageCircle className="w-5 h-5 mx-auto text-[#9334e6] mb-1.5" />
              <span className="text-[10px] font-semibold text-[#9aa0a6] uppercase block">Live Chat</span>
              <span className="text-xs font-medium text-[#202124] dark:text-[#e8eaed]">Mon–Sat, 9am–7pm</span>
            </button>
          </div>
        </div>

        <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-2">Frequently Asked Questions</h3>
          {FAQS.map((f, i) => (
            <div key={i} className="border-b border-[#e8eaed] dark:border-[#3c4043] last:border-b-0">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between py-3.5 text-left cursor-pointer">
                <span className="text-sm font-medium text-[#202124] dark:text-[#e8eaed]">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-[#9aa0a6] shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] pb-3.5">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-4 space-y-4">
        <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-[#1a73e8]" /> Support Hours
          </h3>
          <div className="space-y-2 text-xs text-[#5f6368] dark:text-[#9aa0a6]">
            <div className="flex justify-between"><span>Monday – Friday</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">9am – 7pm</span></div>
            <div className="flex justify-between"><span>Saturday</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">9am – 7pm</span></div>
            <div className="flex justify-between"><span>Sunday</span><span className="font-semibold text-[#202124] dark:text-[#e8eaed]">Closed</span></div>
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#e8f0fe] to-[#d2e3fc] dark:from-[#1a2e4c] dark:to-[#17253b] border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2.5">
            <div className="p-2 rounded-xl bg-white dark:bg-[#303134] text-[#1a73e8] shrink-0">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <p className="text-xs text-[#3c4043] dark:text-[#dadce0] leading-relaxed">
              Urgent issue affecting bookings right now? Call us directly for the fastest response.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TicketsTab() {
  const { showToast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const requestIdRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    return getTickets().then(setTickets).catch(() => setTickets([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function submitTicket(e) {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      const ticket = await createTicket({ subject: subject.trim(), description: description.trim(), category });
      setTickets((prev) => [ticket, ...prev]);
      setSubject('');
      setDescription('');
      showToast(`Ticket ${ticketNumber(ticket._id)} created.`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to raise ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleExpand(id) {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      requestIdRef.current = null;
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    requestIdRef.current = id;
    try {
      const full = await getTicket(id);
      if (requestIdRef.current === id) setDetail(full);
    } catch {
      if (requestIdRef.current === id) setDetail(null);
    } finally {
      if (requestIdRef.current === id) setDetailLoading(false);
    }
  }

  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim() || !detail) return;
    const ticketId = detail._id;
    setReplying(true);
    try {
      const updated = await addTicketComment(ticketId, replyText.trim());
      if (requestIdRef.current === ticketId) setDetail(updated);
      setReplyText('');
    } catch {
      showToast('Failed to send reply', 'error');
    } finally {
      setReplying(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-5 google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-4">Submit a Ticket</h3>
        <form onSubmit={submitTicket} className="space-y-3">
          <div className="google-input-box px-3.5 py-2.5">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]" />
          </div>
          <div className="google-input-box px-3.5 py-2.5">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed] cursor-pointer">
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="google-input-box px-3.5 py-2.5">
            <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your issue..." className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed] resize-none" />
          </div>
          <button type="submit" disabled={submitting} className="w-full py-2.5 rounded-full text-xs font-semibold bg-[#34A853] hover:bg-[#2d8e47] text-white cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Plus className="w-3.5 h-3.5" /> {submitting ? 'Submitting...' : 'New Ticket'}
          </button>
        </form>
      </div>

      <div className="lg:col-span-7">
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-4">Your Tickets ({tickets.length})</h3>
        {loading ? (
          <p className="text-xs text-[#9aa0a6] text-center py-8">Loading tickets...</p>
        ) : tickets.length === 0 ? (
          <div className="google-card py-14 flex flex-col items-center text-center bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <Ticket className="w-8 h-8 text-[#9aa0a6] mb-2" />
            <p className="text-xs text-[#9aa0a6]">No tickets submitted yet.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {tickets.map((t) => {
              const isOpen = expandedId === t._id;
              return (
                <div key={t._id} className="google-card bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] overflow-hidden">
                  <button onClick={() => toggleExpand(t._id)} className="w-full text-left p-4 cursor-pointer">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-[#1a73e8]">{ticketNumber(t._id)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLES[t.status] || STATUS_STYLES.open}`}>{STATUS_LABEL[t.status] || t.status}</span>
                      <span className="text-[10px] text-[#9aa0a6] ml-auto">Updated {formatRelativeTime(t.updatedAt)}</span>
                      <ChevronDown className={`w-3.5 h-3.5 text-[#9aa0a6] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                    <h4 className="text-sm font-medium text-[#202124] dark:text-[#e8eaed] mt-1">{t.subject}</h4>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-[#e8eaed] dark:border-[#3c4043] pt-3 space-y-3">
                      {detailLoading ? (
                        <p className="text-xs text-[#9aa0a6]">Loading...</p>
                      ) : detail ? (
                        <>
                          <p className="text-sm text-[#3c4043] dark:text-[#dadce0]">{detail.description}</p>
                          {detail.comments.map((c) => (
                            <div key={c._id} className="rounded-xl px-3 py-2 bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043]">
                              <p className="text-[11px] font-bold text-[#5f6368] dark:text-[#9aa0a6] mb-0.5">{c.authorName} · {formatRelativeTime(c.createdAt)}</p>
                              <p className="text-sm text-[#202124] dark:text-[#e8eaed]">{c.text}</p>
                            </div>
                          ))}
                          {detail.status !== 'closed' && (
                            <form onSubmit={handleReply} className="flex items-center gap-2">
                              <div className="google-input-box flex-1 px-3 py-2">
                                <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Add a follow-up..." className="w-full bg-transparent text-xs font-medium outline-none text-[#202124] dark:text-[#e8eaed]" />
                              </div>
                              <button type="submit" disabled={replying} className="px-3.5 py-2 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer shrink-0 disabled:opacity-60">
                                Send
                              </button>
                            </form>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatTab() {
  const { showToast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    function load() {
      getChatMessages().then((msgs) => { if (!cancelled) setMessages(msgs); }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    }
    load();
    const interval = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  async function sendChat(e) {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    setSending(true);
    setChatInput('');
    try {
      const msg = await sendChatMessage(text);
      setMessages((prev) => [...prev, msg]);
    } catch {
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <div className="lg:col-span-8 google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full bg-[#34A853]" />
          <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">Support team typically replies within a few hours</span>
        </div>

        <div className="h-80 overflow-y-auto my-4 space-y-2.5 pr-1">
          {loading ? (
            <div className="h-full flex items-center justify-center text-xs text-[#9aa0a6]">Loading conversation...</div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-[#9aa0a6]">Send a message to start the conversation.</div>
          ) : (
            messages.map((m) => (
              <div key={m._id} className={`flex flex-col ${m.from === 'user' ? 'items-end' : 'items-start'}`}>
                {m.from === 'admin' && <span className="text-[10px] font-bold text-[#9aa0a6] mb-0.5 px-1">{m.adminName || 'Support Team'}</span>}
                <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-xs ${m.from === 'user' ? 'bg-[#1a73e8] text-white' : 'bg-[#f1f3f4] dark:bg-[#202124] text-[#202124] dark:text-[#e8eaed]'}`}>
                  {m.text}
                </div>
                <span className="text-[10px] text-[#9aa0a6] mt-1 px-1">{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))
          )}
        </div>

        <form onSubmit={sendChat} className="flex items-center gap-2">
          <div className="google-input-box flex-1 px-3.5 py-2.5">
            <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="Type your message..." className="w-full bg-transparent text-sm outline-none text-[#202124] dark:text-[#e8eaed]" />
          </div>
          <button type="submit" disabled={sending} className="px-4 py-2.5 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer flex items-center gap-1.5 shrink-0 disabled:opacity-60">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </form>
      </div>

      <div className="lg:col-span-4 google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3 flex items-center gap-1.5">
          <MessageSquareText className="w-4 h-4 text-[#1a73e8]" /> Quick Questions
        </h3>
        <div className="space-y-2">
          {QUICK_QUESTIONS.map((q) => (
            <button key={q} onClick={() => setChatInput(q)} className="w-full text-left p-2.5 rounded-xl bg-[#f8fafd] dark:bg-[#202124]/50 border border-[#e8eaed] dark:border-[#3c4043] hover:bg-[#e8f0fe] dark:hover:bg-[#1a2e4c] text-xs text-[#3c4043] dark:text-[#dadce0] transition-colors cursor-pointer">
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SupportPage() {
  const [tab, setTab] = useState('support');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Support</h2>
        <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">Get help or track your support tickets.</p>
      </div>

      <div className="inline-flex p-1 bg-[#f1f3f4] dark:bg-[#202124] rounded-full">
        {[
          { id: 'support', label: 'Support', icon: HelpCircle },
          { id: 'tickets', label: 'Tickets', icon: Ticket },
          { id: 'chat', label: 'Chat', icon: MessageCircle },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${tab === t.id ? 'bg-white dark:bg-[#303134] text-[#1a73e8] shadow-sm' : 'text-[#5f6368] dark:text-[#9aa0a6]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'support' && <SupportTab onOpenChat={() => setTab('chat')} />}
      {tab === 'tickets' && <TicketsTab />}
      {tab === 'chat' && <ChatTab />}
    </div>
  );
}
